import React, { useState, useEffect } from 'react';
import PasswordInput from './password-input';

interface PasswordValidationRule {
  id: string;
  text: string;
  isValid: boolean;
}

interface PasswordInputWithValidationProps {
  label: string;
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  autoComplete?: string;
  showValidation?: boolean;
  disabled?: boolean;
  forceLightMode?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

/**
 * Composant de saisie de mot de passe avec validation en temps réel
 */
const PasswordInputWithValidation: React.FC<PasswordInputWithValidationProps> = ({
  label,
  id,
  name,
  value,
  onChange,
  required = false,
  placeholder = '',
  className = '',
  autoComplete = 'current-password',
  showValidation = true,
  disabled = false,
  forceLightMode = false,
  onValidationChange
}) => {
  // Règles de validation
  const [validationRules, setValidationRules] = useState<PasswordValidationRule[]>([
    { id: 'length', text: 'Au moins 8 caractères', isValid: false },
    { id: 'uppercase', text: 'Contient une lettre majuscule (A-Z)', isValid: false },
    { id: 'lowercase', text: 'Contient une lettre minuscule (a-z)', isValid: false },
    { id: 'digit', text: 'Contient un chiffre (0-9)', isValid: false },
    { id: 'special', text: 'Contient un caractère spécial (!@#$%^&*)', isValid: false }
  ]);

  // Mettre à jour les règles de validation lorsque la valeur change
  useEffect(() => {
    if (value) {
      const updatedRules = [
        { id: 'length', text: 'Au moins 8 caractères', isValid: value.length >= 8 },
        { id: 'uppercase', text: 'Contient une lettre majuscule (A-Z)', isValid: /[A-Z]/.test(value) },
        { id: 'lowercase', text: 'Contient une lettre minuscule (a-z)', isValid: /[a-z]/.test(value) },
        { id: 'digit', text: 'Contient un chiffre (0-9)', isValid: /[0-9]/.test(value) },
        { id: 'special', text: 'Contient un caractère spécial (!@#$%^&*)', isValid: /[!@#$%^&*(),.?":{}|<>]/.test(value) }
      ];
      
      setValidationRules(updatedRules);
      
      // Informer le parent si toutes les règles sont valides
      if (onValidationChange) {
        const isAllValid = updatedRules.every(rule => rule.isValid);
        onValidationChange(isAllValid);
      }
    }
  }, [value, onValidationChange]);

  return (
    <div className={`space-y-1 ${className}`}>
      <PasswordInput
        label={label}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        forceLightMode={forceLightMode}
      />
      
      {showValidation && value && (
        <div className="mt-2 text-sm space-y-1">
          {validationRules.map(rule => (
            <div key={rule.id} className="flex items-center">
              {rule.isValid ? (
                <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-4 w-4 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className={rule.isValid ? `text-green-600 ${!forceLightMode ? 'dark:text-green-400' : ''}` : `text-red-600 ${!forceLightMode ? 'dark:text-red-400' : ''}`}>
                {rule.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordInputWithValidation;
